/**
 * BillDrawer Component
 * Create/Edit/View Bill form
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  Package,
  Plus,
  Receipt,
  Trash2,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetCostCodesQuery } from '../../services/costCodeApi';
import { useCreateBillMutation, useUpdateBillMutation } from '../../services/billApi';
import { useGetVendorsQuery } from '../../services/vendorApi';
import type { CostCode } from '../../types/costCode';
import type { Bill, BillVendor, CreateBillRequest, UpdateBillRequest } from '../../types/bill';
import type { Vendor } from '../../types/vendor';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  Input,
  NumberInput,
  PhoneInput,
  SearchableDropdown,
  TextArea,
} from '../forms';

// Props interface
export interface BillDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  bill?: Bill | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// Line item form data
interface LineItemFormData {
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  costCode?: string;
  taxable: boolean;
  notes?: string;
}

// Form data type
interface BillFormData {
  title: string;
  vendorId: string;
  vendorName: string;
  vendorContactName: string;
  vendorEmail: string;
  vendorPhone: string;
  vendorAddress: string;
  billDate: string;
  dueDate: string;
  receivedDate: string;
  paymentTerms: string;
  taxRate: number;
  currency: string;
  notes: string;
  internalNotes: string;
  lineItems: LineItemFormData[];
  costCodeId: string;
  budgetId: string;
}

// Validation schema
const lineItemSchema = Yup.object({
  description: Yup.string().required('Description is required'),
  quantity: Yup.number().required('Quantity is required').min(0.01, 'Quantity must be positive'),
  unit: Yup.string().required('Unit is required'),
  unitCost: Yup.number().required('Unit cost is required').min(0, 'Unit cost must be positive'),
  costCode: Yup.string().optional(),
  taxable: Yup.boolean().default(true),
  notes: Yup.string().optional(),
});

const billSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  vendorId: Yup.string().optional(),
  vendorName: Yup.string().required('Vendor name is required'),
  vendorContactName: Yup.string().optional(),
  vendorEmail: Yup.string().email('Invalid email').optional(),
  vendorPhone: Yup.string().optional(),
  vendorAddress: Yup.string().optional(),
  billDate: Yup.string().required('Bill date is required'),
  dueDate: Yup.string().optional(),
  receivedDate: Yup.string().optional(),
  paymentTerms: Yup.string().optional(),
  taxRate: Yup.number().min(0).max(100, 'Tax rate cannot exceed 100%').optional(),
  currency: Yup.string().required('Currency is required'),
  notes: Yup.string().optional(),
  internalNotes: Yup.string().optional(),
  lineItems: Yup.array().of(lineItemSchema).min(1, 'At least one line item is required'),
  costCodeId: Yup.string().optional(),
  budgetId: Yup.string().optional(),
});

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
];

// Unit options
const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'unit', label: 'Unit' },
  { value: 'box', label: 'Box' },
  { value: 'pack', label: 'Pack' },
  { value: 'kg', label: 'Kilogram' },
  { value: 'lb', label: 'Pound' },
  { value: 'ton', label: 'Ton' },
  { value: 'm', label: 'Meter' },
  { value: 'ft', label: 'Feet' },
  { value: 'sqm', label: 'Square Meter' },
  { value: 'sqft', label: 'Square Feet' },
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'lot', label: 'Lot' },
  { value: 'ls', label: 'Lump Sum' },
];

// Payment terms options
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Select payment terms' },
  { value: 'Net 15', label: 'Net 15' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 45', label: 'Net 45' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
  { value: 'COD', label: 'Cash on Delivery' },
];

// Get today's date in ISO format
const getTodayISO = (): string => {
  return new Date().toISOString().split('T')[0];
};

const BillDrawer: React.FC<BillDrawerProps> = ({ isOpen, onClose, bill, mode, onSuccess }) => {
  const projectId = useCurrentProjectId();
  const { success, error: showError } = useToast();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    vendor: true,
    lineItems: true,
    financial: true,
    payment: false,
    notes: false,
  });

  // API mutations
  const [createBill, { isLoading: isCreating }] = useCreateBillMutation();
  const [updateBill, { isLoading: isUpdating }] = useUpdateBillMutation();
  const isSubmitting = isCreating || isUpdating;

  // Cost codes query
  const { data: costCodesResponse } = useGetCostCodesQuery({ isActive: true, limit: 500 });
  const costCodeOptions = useMemo(() => {
    const costCodes = costCodesResponse?.data?.costCodes || [];
    return costCodes.map((cc: CostCode) => ({
      value: cc._id,
      label: `${cc.code} - ${cc.description}`,
    }));
  }, [costCodesResponse]);

  // Vendors query
  const { data: vendorsResponse } = useGetVendorsQuery({ status: 'active', limit: 500 });
  const vendors = useMemo(() => vendorsResponse?.data?.vendors || [], [vendorsResponse]);
  const vendorOptions = useMemo(() => {
    return vendors.map((v: Vendor) => ({
      value: v._id,
      label: `${v.code} - ${v.name}`,
    }));
  }, [vendors]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BillFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(billSchema) as any,
    defaultValues: {
      title: '',
      vendorId: '',
      vendorName: '',
      vendorContactName: '',
      vendorEmail: '',
      vendorPhone: '',
      vendorAddress: '',
      billDate: getTodayISO(),
      dueDate: '',
      receivedDate: '',
      paymentTerms: '',
      taxRate: 0,
      currency: 'USD',
      notes: '',
      internalNotes: '',
      lineItems: [{ description: '', quantity: 1, unit: 'each', unitCost: 0, taxable: true }],
      costCodeId: '',
      budgetId: '',
    },
  });

  // Line items field array
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  // Watch line items for total calculation
  const watchedLineItems = watch('lineItems');
  const watchedTaxRate = watch('taxRate');

  // Calculate totals
  const { subtotal, taxAmount, total } = useMemo(() => {
    const sub =
      watchedLineItems?.reduce((acc, item) => {
        const qty = item?.quantity || 0;
        const cost = item?.unitCost || 0;
        return acc + qty * cost;
      }, 0) || 0;
    const tax = sub * ((watchedTaxRate || 0) / 100);
    return { subtotal: sub, taxAmount: tax, total: sub + tax };
  }, [watchedLineItems, watchedTaxRate]);

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Reset form when bill changes
  useEffect(() => {
    if (bill && (isEditMode || isViewMode)) {
      reset({
        title: bill.title || '',
        vendorId: bill.vendorId || '',
        vendorName: bill.vendor.name,
        vendorContactName: bill.vendor.contactName || '',
        vendorEmail: bill.vendor.email || '',
        vendorPhone: bill.vendor.phone || '',
        vendorAddress: bill.vendor.address || '',
        billDate: bill.billDate?.split('T')[0] || getTodayISO(),
        dueDate: bill.dueDate?.split('T')[0] || '',
        receivedDate: bill.receivedDate?.split('T')[0] || '',
        paymentTerms: bill.paymentTerms || '',
        taxRate: bill.taxRate || 0,
        currency: bill.currency || 'USD',
        notes: bill.notes || '',
        internalNotes: bill.internalNotes || '',
        lineItems: bill.lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          costCode: item.costCodeId || '',
          taxable: item.taxable,
          notes: item.notes || '',
        })),
        costCodeId: bill.costCodeId || '',
        budgetId: bill.budgetId || '',
      });
    } else if (mode === 'create') {
      reset({
        title: '',
        vendorId: '',
        vendorName: '',
        vendorContactName: '',
        vendorEmail: '',
        vendorPhone: '',
        vendorAddress: '',
        billDate: getTodayISO(),
        dueDate: '',
        receivedDate: '',
        paymentTerms: '',
        taxRate: 0,
        currency: 'USD',
        notes: '',
        internalNotes: '',
        lineItems: [{ description: '', quantity: 1, unit: 'each', unitCost: 0, taxable: true }],
        costCodeId: '',
        budgetId: '',
      });
    }
  }, [bill, mode, reset, isEditMode, isViewMode]);

  // Handle vendor selection
  const handleVendorSelect = (vendorId: string) => {
    setValue('vendorId', vendorId);
    if (vendorId) {
      const selectedVendor = vendors.find((v: Vendor) => v._id === vendorId);
      if (selectedVendor) {
        setValue('vendorName', selectedVendor.name);
        setValue('vendorContactName', selectedVendor.contact?.name || '');
        setValue('vendorEmail', selectedVendor.contact?.email || '');
        setValue('vendorPhone', selectedVendor.contact?.phone || '');
        const addr = selectedVendor.address;
        const addressParts = [
          addr?.street,
          addr?.city,
          addr?.state,
          addr?.zipCode,
          addr?.country,
        ].filter(Boolean);
        setValue('vendorAddress', addressParts.join(', '));
      }
    } else {
      setValue('vendorName', '');
      setValue('vendorContactName', '');
      setValue('vendorEmail', '');
      setValue('vendorPhone', '');
      setValue('vendorAddress', '');
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data: BillFormData) => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    try {
      const vendor: BillVendor = {
        name: data.vendorName,
        contactName: data.vendorContactName || undefined,
        email: data.vendorEmail || undefined,
        phone: data.vendorPhone || undefined,
        address: data.vendorAddress || undefined,
      };

      if (mode === 'create') {
        const createData: CreateBillRequest = {
          projectId,
          title: data.title,
          vendorId: data.vendorId || undefined,
          vendor,
          billDate: data.billDate,
          dueDate: data.dueDate || undefined,
          receivedDate: data.receivedDate || undefined,
          lineItems: data.lineItems.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costCodeId: item.costCode || undefined,
            taxable: item.taxable,
            notes: item.notes || undefined,
          })),
          taxRate: data.taxRate || undefined,
          currency: data.currency,
          paymentTerms: data.paymentTerms || undefined,
          notes: data.notes || undefined,
          internalNotes: data.internalNotes || undefined,
          costCodeId: data.costCodeId || undefined,
          budgetId: data.budgetId || undefined,
        };

        await createBill({ projectId, data: createData }).unwrap();
        success('Bill created successfully');
      } else if (mode === 'edit' && bill) {
        const updateData: UpdateBillRequest = {
          title: data.title,
          vendorId: data.vendorId || undefined,
          vendor,
          billDate: data.billDate,
          dueDate: data.dueDate || undefined,
          receivedDate: data.receivedDate || undefined,
          lineItems: data.lineItems.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costCodeId: item.costCode || undefined,
            taxable: item.taxable,
            notes: item.notes || undefined,
          })),
          taxRate: data.taxRate || undefined,
          currency: data.currency,
          paymentTerms: data.paymentTerms || undefined,
          notes: data.notes || undefined,
          internalNotes: data.internalNotes || undefined,
          costCodeId: data.costCodeId || undefined,
          budgetId: data.budgetId || undefined,
        };

        await updateBill({
          projectId,
          billId: bill._id,
          data: updateData,
        }).unwrap();
        success('Bill updated successfully');
      }

      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as {
        data?: { message?: string; errors?: Array<{ field: string; message: string }> };
        message?: string;
      };
      if (error.data?.errors) {
        const errorMessages = error.data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
        showError(errorMessages);
      } else {
        showError(error.data?.message || error.message || 'Failed to save bill');
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addLineItem = () => {
    append({ description: '', quantity: 1, unit: 'each', unitCost: 0, taxable: true });
  };

  const removeLineItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: watch('currency') || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Drawer - Large size for multiple sections */}
      <div className="fixed top-6 right-6 bottom-6 left-6 z-[100] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <Receipt className="h-5 w-5 text-brand-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create'
                    ? 'Create Bill'
                    : mode === 'edit'
                      ? 'Edit Bill'
                      : `Bill: ${bill?.billNumber}`}
                </h2>
                {bill && <p className="text-sm text-gray-500">{bill.vendor.name}</p>}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Basic Information Section */}
              <section className="space-y-4">
                <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Basic Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Title"
                        value={field.value}
                        onChange={e => field.onChange(e.target.value)}
                        error={errors.title?.message}
                        disabled={isViewMode}
                        placeholder="Enter bill title/description"
                        required
                      />
                    )}
                  />

                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Currency"
                        options={CURRENCY_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.currency?.message}
                        disabled={isViewMode}
                        required
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Controller
                    name="billDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Bill Date"
                        value={field.value}
                        onChange={date => field.onChange(date || '')}
                        error={errors.billDate?.message}
                        disabled={isViewMode}
                        required
                        placeholder="Select bill date"
                      />
                    )}
                  />

                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Due Date"
                        value={field.value}
                        onChange={date => field.onChange(date || '')}
                        error={errors.dueDate?.message}
                        disabled={isViewMode}
                        placeholder="Select due date"
                      />
                    )}
                  />

                  <Controller
                    name="receivedDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Received Date"
                        value={field.value}
                        onChange={date => field.onChange(date || '')}
                        error={errors.receivedDate?.message}
                        disabled={isViewMode}
                        placeholder="When bill was received"
                      />
                    )}
                  />
                </div>
              </section>

              {/* Vendor Information Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('vendor')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Vendor Information
                    </h3>
                  </div>
                  {expandedSections.vendor ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.vendor && (
                  <div className="space-y-4 pt-2">
                    <Controller
                      name="vendorId"
                      control={control}
                      render={({ field }) => (
                        <SearchableDropdown
                          label="Select Vendor"
                          options={[
                            { value: '', label: 'Select a vendor or enter manually...' },
                            ...vendorOptions,
                          ]}
                          value={field.value}
                          onChange={value => handleVendorSelect(value)}
                          disabled={isViewMode}
                          placeholder="Search vendors..."
                        />
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="vendorName"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Vendor Name"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.vendorName?.message}
                            disabled={isViewMode}
                            required
                            placeholder="Enter vendor name"
                          />
                        )}
                      />

                      <Controller
                        name="vendorContactName"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Contact Name"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.vendorContactName?.message}
                            disabled={isViewMode}
                            placeholder="Enter contact person"
                          />
                        )}
                      />

                      <Controller
                        name="vendorEmail"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Email"
                            type="email"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.vendorEmail?.message}
                            disabled={isViewMode}
                            placeholder="vendor@example.com"
                          />
                        )}
                      />

                      <Controller
                        name="vendorPhone"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            label="Phone"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            error={errors.vendorPhone?.message}
                            disabled={isViewMode}
                          />
                        )}
                      />
                    </div>

                    <Controller
                      name="vendorAddress"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Address"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.vendorAddress?.message}
                          disabled={isViewMode}
                          placeholder="Enter vendor address"
                          rows={2}
                        />
                      )}
                    />
                  </div>
                )}
              </section>

              {/* Line Items Section */}
              <section className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Line Items
                    </h3>
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="btn btn-secondary btn-sm"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </button>
                  )}
                </div>

                {errors.lineItems &&
                  typeof errors.lineItems === 'object' &&
                  'message' in errors.lineItems && (
                    <div className="flex items-center space-x-2 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{errors.lineItems.message}</span>
                    </div>
                  )}

                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">Item {index + 1}</h4>
                        {!isViewMode && fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                          <Controller
                            name={`lineItems.${index}.description`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Input
                                label="Description"
                                value={inputField.value}
                                onChange={e => inputField.onChange(e.target.value)}
                                error={errors.lineItems?.[index]?.description?.message}
                                disabled={isViewMode}
                                required
                                placeholder="Item description"
                              />
                            )}
                          />
                        </div>

                        <Controller
                          name={`lineItems.${index}.quantity`}
                          control={control}
                          render={({ field: inputField }) => (
                            <NumberInput
                              label="Quantity"
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.quantity?.message}
                              disabled={isViewMode}
                              required
                              min={0.01}
                              step={0.01}
                            />
                          )}
                        />

                        <Controller
                          name={`lineItems.${index}.unit`}
                          control={control}
                          render={({ field: inputField }) => (
                            <Dropdown
                              label="Unit"
                              options={UNIT_OPTIONS}
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.unit?.message}
                              disabled={isViewMode}
                              required
                            />
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Controller
                          name={`lineItems.${index}.unitCost`}
                          control={control}
                          render={({ field: inputField }) => (
                            <CurrencyInput
                              label="Unit Cost"
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.unitCost?.message}
                              disabled={isViewMode}
                              required
                            />
                          )}
                        />

                        <Controller
                          name={`lineItems.${index}.costCode`}
                          control={control}
                          render={({ field: inputField }) => (
                            <SearchableDropdown
                              label="Cost Code"
                              options={costCodeOptions}
                              value={inputField.value || ''}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.costCode?.message}
                              disabled={isViewMode}
                              placeholder="Select cost code..."
                            />
                          )}
                        />

                        <div className="md:col-span-2">
                          <Controller
                            name={`lineItems.${index}.notes`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Input
                                label="Notes"
                                value={inputField.value || ''}
                                onChange={e => inputField.onChange(e.target.value)}
                                error={errors.lineItems?.[index]?.notes?.message}
                                disabled={isViewMode}
                                placeholder="Line item notes"
                              />
                            )}
                          />
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-sm font-medium text-gray-700">
                          Line Total:{' '}
                          <span className="text-gray-900">
                            {formatCurrency(
                              (watchedLineItems?.[index]?.quantity || 0) *
                                (watchedLineItems?.[index]?.unitCost || 0)
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Financial Summary Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('financial')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Financial Summary
                    </h3>
                  </div>
                  {expandedSections.financial ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.financial && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Controller
                        name="taxRate"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Tax Rate (%)"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.taxRate?.message}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="costCodeId"
                        control={control}
                        render={({ field }) => (
                          <SearchableDropdown
                            label="Primary Cost Code"
                            options={costCodeOptions}
                            value={field.value || ''}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            placeholder="Select cost code..."
                          />
                        )}
                      />

                      <div className="flex flex-col justify-end">
                        <label className="block text-xs text-gray-500 mb-1">Subtotal</label>
                        <div className="text-lg font-medium text-gray-900">
                          {formatCurrency(subtotal)}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-100 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="text-gray-900">{formatCurrency(subtotal)}</span>
                      </div>
                      {watchedTaxRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tax ({watchedTaxRate}%):</span>
                          <span className="text-gray-900">{formatCurrency(taxAmount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-semibold border-t border-gray-300 pt-2 mt-2">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-brand-600">{formatCurrency(total)}</span>
                      </div>
                      {bill && bill.paidAmount > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Paid:</span>
                            <span>{formatCurrency(bill.paidAmount)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium text-orange-600">
                            <span>Balance Due:</span>
                            <span>{formatCurrency(bill.remainingBalance || 0)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </section>

              {/* Payment Terms Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('payment')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Payment Terms
                    </h3>
                  </div>
                  {expandedSections.payment ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.payment && (
                  <div className="space-y-4 pt-2">
                    <Controller
                      name="paymentTerms"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="Payment Terms"
                          options={PAYMENT_TERMS_OPTIONS}
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.paymentTerms?.message}
                          disabled={isViewMode}
                        />
                      )}
                    />

                    {bill && isViewMode && (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {bill.paymentMethod && (
                          <div>
                            <span className="text-gray-500">Payment Method:</span>
                            <span className="ml-2 text-gray-900">{bill.paymentMethod}</span>
                          </div>
                        )}
                        {bill.paymentReference && (
                          <div>
                            <span className="text-gray-500">Reference:</span>
                            <span className="ml-2 text-gray-900">{bill.paymentReference}</span>
                          </div>
                        )}
                        {bill.paidDate && (
                          <div>
                            <span className="text-gray-500">Paid Date:</span>
                            <span className="ml-2 text-gray-900">
                              {new Date(bill.paidDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Notes Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('notes')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Notes
                    </h3>
                  </div>
                  {expandedSections.notes ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.notes && (
                  <div className="space-y-4 pt-2">
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="External Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.notes?.message}
                          disabled={isViewMode}
                          placeholder="Notes about this bill"
                          rows={2}
                        />
                      )}
                    />

                    <Controller
                      name="internalNotes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Internal Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.internalNotes?.message}
                          disabled={isViewMode}
                          placeholder="Internal notes (not visible externally)"
                          rows={2}
                          hint="These notes are only visible to your team"
                        />
                      )}
                    />
                  </div>
                )}
              </section>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex justify-between items-center">
              <button type="button" onClick={handleClose} className="btn btn-secondary">
                {isViewMode ? 'Close' : 'Cancel'}
              </button>

              {!isViewMode && (
                <button
                  type="submit"
                  onClick={handleSubmit(handleFormSubmit)}
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      </span>
                      {mode === 'create' ? 'Creating...' : 'Saving...'}
                    </>
                  ) : (
                    <>{mode === 'create' ? 'Create Bill' : 'Save Changes'}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default BillDrawer;
